'use client';
import { FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { twJoin, twMerge } from 'tailwind-merge';

import { Button, Heading, Svg, Text } from '@/components';
import { HeartOutlineIcon } from '@/icons';
import { useCart } from '@/context/cartContext';
import { useToggle } from '@/hooks';
import { IMAGE_URI } from '@/config/endpoints';
import {
  checkItemInWishlist,
  getBasicCartRequest,
  getKeyValue,
  getUrlParamsStr,
  getWeightWithUnit,
  googleDataLayerEventPush,
} from '@/utils/helper';
import { OUT_OF_STOCK_LABEL, PROUDCT_ATTRIBUTE_SETS_ENUMS } from '@/constants';
import { useWishlist } from '@/context/wishlistContext';
import { ToggleCountField } from '../Inputs';
import { usePathname } from 'next/navigation';
import { searchParams } from '@/utils/helper';
import { storage } from '@/config';
import SelectOptionProductCard from '@/features/ProductDetails/components/SelectOptionProductCard';
import {
  ENTITY_TYPE_ENUMS,
  PRODUCT_TYPE_ENUMS,
} from '@/features/ProductDetails/constants/ProductType';
import { useCleverTap } from '@/context/clevertapContext';
import {
  buildAddedToCartEventData,
  getProductCategory,
  getEffectivePrice,
  getProductImageUrl,
} from '@/utils/clevertapEvents';

interface PropTypes {
  item: any;
  inShop?: boolean;
  toggleCartSidebar?: (value: boolean) => void;
  classNames?: { root?: string };
  plpCard?: boolean;
  eventMetadata?: {
    sectionName?: string;
    rank?: number;
  };
  dataLayerSource?:
    | 'ARGOID_CART_RECOMMENDATIONS'
    | 'ARGOID_PLP'
    | 'ARGOID_SEARCH_DIRECT'
    | 'ARGOID_SEARCH_AUTOCOMPLETE'
    | 'ARGOID_HOME_TRENDING'
    | 'ARGOID_PDP_SIMILAR_PRODUCTS'
    | 'ARGOID_PDP_COMPLEMENTING_PRODUCTS';
  searchResult?: 'DIRECT' | 'AUTO_COMPLETE';
  index?: number;
}

interface ButtonPropTypes {
  label: 'Add to Cart' | 'Added to Cart';
  disabled?: boolean;
}

const ProductCard: FC<PropTypes> = ({
  item,
  inShop,
  toggleCartSidebar,
  classNames,
  plpCard,
  eventMetadata,
  dataLayerSource,
  searchResult,
  index,
}) => {
  const [selectedVariant, setSelectedVariant] = useState<any>(item);
  const customAttributes = getKeyValue(
    item?.custom_attributes,
    'attribute_code'
  );
  const tag_one = item?.tag_one || customAttributes?.tag_one;
  const tag_two = item?.tag_two || customAttributes?.tag_two;
  const url_key = item?.url_key ?? customAttributes?.url_key;
  const entityType =
    customAttributes?.entityType ??
    (item?.entityType || item?.extension_attributes?.attribute_set_name);
  const { cartItems, cartItemsMap, cartValue, addToCart, removeFromCart } =
    useCart();
  const { pushEvent } = useCleverTap();
  const pathname = usePathname();
  const query = searchParams.get('query');
  const { addToWishlist, removeFromWishlist, wishlistItems } = useWishlist();
  const [itemId, setItemId] = useState<number>();
  const [qtyInCart, setQtyFromCart] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [variantsOptions, setVariantsOptions] = useState<any>([]);
  const itemExistsInCart = cartItemsMap?.get(selectedVariant.sku);
  const [disableAtc, setDisableAtc] = useState<boolean>(false);

  const [buttonProps, setButtonProps] = useState<ButtonPropTypes>({
    label: 'Add to Cart',
    disabled: false,
  });

  const quote_id = storage.get('quote_id');

  const isWishlisted = checkItemInWishlist(wishlistItems, selectedVariant?.sku);

  const { value: isInCart } = useToggle(false);

  const itemInCart = () => {
    if (itemExistsInCart) {
      setItemId(itemExistsInCart?.item_id);
      setQtyFromCart(itemExistsInCart?.qty);
      setButtonProps({ label: 'Added to Cart' });
    } else {
      setButtonProps({
        label: 'Add to Cart',
      });
    }
  };

  useEffect(() => {
    itemInCart();
  }, [cartItems, selectedVariant]);

  useEffect(() => {
    if (
      item.typeId === PRODUCT_TYPE_ENUMS.configurable ||
      item?.type_id === PRODUCT_TYPE_ENUMS.configurable
    ) {
      const relatedVariants = item.relatedVariants
        ? JSON.parse(item.relatedVariants)
        : [];

      if (!relatedVariants.length) {
        setSelectedVariant(item);
        return;
      }
      const relatedVariantsOptions = relatedVariants.map((variant: any) => {
        return {
          ...item,
          label: getWeightWithUnit(variant.weight, variant?.weight_unit ?? ''),
          value: variant.weight,
          price: variant.currentPrice,
          weight: variant.weight,
          weight_unit: variant.weight_unit,
          currentPrice: variant.currentPrice,
          name: variant.name,
          sku: variant.sku,
          stock_status: variant?.stock_status,
          size: variant?.size,
        };
      });

      // sort the variants by price in ascending order
      const varientsAsc = relatedVariantsOptions.sort((a: any, b: any) => {
        const priceDiff = Number(a.price) - Number(b.price);
        if (priceDiff !== 0) {
          // If prices are different, sort by price difference
          return priceDiff;
        } else {
          // If prices are equal, sort by the elements themselves
          return (
            relatedVariantsOptions.indexOf(a) -
            relatedVariantsOptions.indexOf(b)
          );
        }
      });

      // sort the variants with in-stock first while maintaining the original order
      const sortedVariantsWithStockInfo = varientsAsc.sort((a: any, b: any) => {
        if (
          a.stock_status !== 'OUT_OF_STOCK' &&
          b.stock_status === 'OUT_OF_STOCK'
        ) {
          return -1; // a comes before b
        } else if (
          a.stock_status === 'OUT_OF_STOCK' &&
          b.stock_status !== 'OUT_OF_STOCK'
        ) {
          return 1; // b comes before a
        } else {
          return 0; // maintain the original order
        }
      });

      if (!sortedVariantsWithStockInfo.length) return;
      setVariantsOptions(sortedVariantsWithStockInfo);
      setSelectedVariant(sortedVariantsWithStockInfo[0]);
    } else {
      setSelectedVariant(item);
    }
  }, [item]);

  const image = customAttributes?.thumbnail;
  const imageUrl = !image
    ? ''
    : `${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${image}`;

  const request = useMemo(() => {
    const req = getBasicCartRequest(selectedVariant);
    if (quote_id) {
      req.quote_id = quote_id;
    }
    if (
      entityType === PROUDCT_ATTRIBUTE_SETS_ENUMS.giftHampers &&
      item?.options
    ) {
      const attributes =
        typeof item?.options === 'string'
          ? JSON.parse(item?.options)?.[0]
          : item?.options?.[0];
      req.gift.option_id = attributes?.option_id;
      req.gift.option_value = '1';
    }
    if (
      item.type_id === PRODUCT_TYPE_ENUMS.configurable ||
      item.typeId === PRODUCT_TYPE_ENUMS.configurable
    ) {
      req.sku = item?.sku;
      if (selectedVariant?.selectedSize) {
        req.size.option_value = selectedVariant?.selectedSize;
      }
    }
    itemInCart();
    return req;
  }, [selectedVariant, quote_id]);

  const onCart = async (e: MouseEvent<HTMLButtonElement>) => {
    const searchQuery = (
      document.getElementById('searchBar') as HTMLInputElement
    )?.value;
    e?.preventDefault();
    e?.stopPropagation();
    setDisableAtc(true);
    const req = await addToCart(request, 'post', false);
    setDisableAtc(false);
    if (req.success) {
      if (dataLayerSource) {
        googleDataLayerEventPush({
          eventName: 'add_to_cart',
          eventParameters: {
            currency: 'INR',
            eventSource: dataLayerSource,
            value: customAttributes?.special_price
              ? customAttributes?.special_price
              : selectedVariant?.price,
            items: [selectedVariant],
          },
        });
      }
      const atcEventData = buildAddedToCartEventData({
        productName: item?.name,
        productId: request?.sku,
        productPrice: getEffectivePrice(
          customAttributes,
          selectedVariant?.price
        ),
        quantity: '1',
        cartAmount:
          cartValue +
          Number(getEffectivePrice(customAttributes, selectedVariant?.price)),
        source: 'PLP',
        category: getProductCategory(item, customAttributes),
        productImageUrl: getProductImageUrl(customAttributes),
        pathname,
        section: eventMetadata?.sectionName,
      });
      pushEvent({
        event: 'Added Product To Cart',
        data: atcEventData,
      });
      if (eventMetadata?.sectionName) {
        const dataToPush: any = {
          'Section Name': eventMetadata?.sectionName,
          Type: 'Add to Cart',
          ['Product Name']: item?.name,
          ['Product ID']: request?.sku,
          ['Product Price']: customAttributes?.special_price
            ? customAttributes?.special_price
            : selectedVariant?.price,
          Pathname: pathname,
        };
        if (eventMetadata?.rank) {
          dataToPush['Rank'] = eventMetadata?.rank;
        }
        pushEvent({
          event: 'Product Carousel Clicked',
          data: dataToPush,
        });
      }
      if (searchResult) {
        pushEvent({
          event: 'Search Result Add To Cart',
          data: {
            'Search Keyword': searchQuery,
            'Search Type': searchResult,
            'Product SKU': item?.sku,
            'Product Name': item?.name,
            'Product Type': 'Product',
            'Product ID': item?.id,
            Position: index !== undefined ? index + 1 : undefined,
          },
        });
      }
    }
  };

  const UserActions = () => {
    return (
      <>
        {!itemExistsInCart ? (
          <div
            className={twMerge(
              'relative w-full transition duration-200 ease-in-out'
            )}>
            <Button
              id='add-to-cart-button'
              style={{ backgroundColor: '#A0383F' }}
              className={twMerge(
                'py-[0.94rem] text-sm font-bold disabled:opacity-100 h-[44px]',
                plpCard ? 'max-md:py-[0.94rem]' : '',
                'hover:opacity-90'
              )}
              label={buttonProps.label}
              onClick={onCart}
              disabled={
                disableAtc ||
                itemExistsInCart ||
                selectedVariant?.stock_status === 'OUT_OF_STOCK'
              }
            />
          </div>
        ) : (
          <div className='h-full w-full rounded-full bg-pale-rose'>
            <ToggleCountField
              disabled={
                isLoading || selectedVariant?.stock_status === 'OUT_OF_STOCK'
              }
              OutofStock={selectedVariant?.stock_status === 'OUT_OF_STOCK'}
              getCount={async (newValue: number) => {
                setIsLoading(true);
                request.qty = newValue;
                request.item_id = itemId;
                if (newValue === 0) {
                  await removeFromCart(request);
                  setIsLoading(false);
                  return;
                }
                const req = await addToCart(request, 'put');
                if (req.success) {
                  pushEvent({
                    event: 'Updated Product In Cart',
                    data: {
                      ['Timestamp']: new Date(),
                      ['Product Name']: item?.name,
                      ['Product ID']: request?.sku,
                      ['Product Price']: customAttributes?.special_price
                        ? customAttributes?.special_price
                        : selectedVariant?.price,
                      ['Updated Quantity']: newValue,
                      ['Cart Amount']:
                        itemExistsInCart?.qty &&
                        newValue > itemExistsInCart?.qty
                          ? cartValue +
                            Number(
                              customAttributes?.special_price
                                ? customAttributes?.special_price
                                : item?.price
                            )
                          : cartValue -
                            Number(
                              customAttributes?.special_price
                                ? customAttributes?.special_price
                                : item?.price
                            ),
                    },
                  });
                }
                setIsLoading(false);
              }}
              defaultValue={qtyInCart}
              variant='PLP'
            />
          </div>
        )}
      </>
    );
  };

  async function toggleWishlist(e: MouseEvent<HTMLButtonElement>) {
    const searchQuery = (
      document.getElementById('searchBar') as HTMLInputElement
    )?.value;
    e.preventDefault();
    e.stopPropagation();
    if (!isWishlisted) {
      addToWishlist(selectedVariant);
      if (eventMetadata?.sectionName) {
        const dataToPush: any = {
          'Section Name': eventMetadata?.sectionName,
          Type: 'Wishlist',
          ['Product Name']: item?.name,
          ['Product ID']: request?.sku,
          ['Product Price']: customAttributes?.special_price
            ? customAttributes?.special_price
            : selectedVariant?.price,
          Pathname: pathname,
        };
        if (eventMetadata?.rank) {
          dataToPush['Rank'] = eventMetadata?.rank;
        }
        pushEvent({
          event: 'Product Carousel Clicked',
          data: dataToPush,
        });
      }
      if (searchResult) {
        pushEvent({
          event: 'Search Result Wishlist',
          data: {
            'Search Keyword': searchQuery,
            'Search Type': searchResult,
            'Product SKU': item?.sku,
            'Product Name': item?.name,
            'Product Type': 'Product',
          },
        });
      }
    } else {
      removeFromWishlist(selectedVariant);
    }
  }

  const pageParams: any = {};
  if (item?.type_id === PRODUCT_TYPE_ENUMS.configurable) {
    pageParams.selected = selectedVariant?.sku;
  }
  if (inShop) {
    pageParams.breadcrumb = `${pathname}/`;
  }
  if (query) {
    pageParams.query = query;
  }

  function handleVariantChange(option: any) {
    setSelectedVariant(option);
  }

  return (
    <div
      onClick={() => {
        const searchQuery = (
          document.getElementById('searchBar') as HTMLInputElement
        )?.value;
        if (dataLayerSource) {
          googleDataLayerEventPush({
            eventName: 'select_item',
            eventParameters: {
              currency: 'INR',
              eventSource: dataLayerSource,
              value: customAttributes?.special_price
                ? customAttributes?.special_price
                : selectedVariant?.price,
              items: [selectedVariant],
            },
          });
        }
        if (searchResult) {
          pushEvent({
            event: 'Search Result Clicked',
            data: {
              'Search Keyword': searchQuery,
              'Search Type': searchResult,
              'Product SKU': item?.sku,
              'Product Name': item?.name,
              'Product Type': 'Product',
            },
          });
        }
      }}
      key={selectedVariant.id}
      className={twMerge(
        'group col-span-1 row-span-1 flex w-full max-w-76.5 flex-1 flex-col gap-6 md:gap-9',
        classNames?.root
      )}>
      <Link
        href={
          entityType === ENTITY_TYPE_ENUMS.GIFT_HAMPERS
            ? `/gifting/product/${url_key}/${
                Object.keys(pageParams).length
                  ? `?${getUrlParamsStr(pageParams)}`
                  : ''
              }`
            : item.type_id === PRODUCT_TYPE_ENUMS.configurable
              ? `/shop/product/${url_key}/${
                  Object.keys(pageParams).length
                    ? `?${getUrlParamsStr(pageParams)}`
                    : ''
                }`
              : `/shop/product/${url_key}/${
                  Object.keys(pageParams).length
                    ? `?${getUrlParamsStr(pageParams)}`
                    : ''
                }`
        }
        passHref
        prefetch={false}
        className='block space-y-2'
        onClick={() => {
          if (toggleCartSidebar) toggleCartSidebar(false);
          if (eventMetadata?.sectionName) {
            const dataToPush: any = {
              'Section Name': eventMetadata?.sectionName,
              Type: 'Open PDP',
              ['Product Name']: item?.name,
              ['Product ID']: request?.sku,
              ['Product Price']: customAttributes?.special_price
                ? customAttributes?.special_price
                : selectedVariant?.price,
              Pathname: pathname,
            };
            if (eventMetadata?.rank) {
              dataToPush['Rank'] = eventMetadata?.rank;
            }
            pushEvent({
              event: 'Product Carousel Clicked',
              data: dataToPush,
            });
          }
        }}>
        <div
          className={twMerge(
            'relative flex items-center justify-center rounded-lg bg-[#fef9f8] p-0'
          )}>
          {selectedVariant?.stock_status === 'OUT_OF_STOCK' && (
            <Text className='md:text-md absolute left-3 top-4 z-10 rounded-sm bg-[#38383A] p-3 px-2 py-1 text-xs text-white sm:text-sm'>
              {OUT_OF_STOCK_LABEL}
            </Text>
          )}

          <button
            className={twMerge(
              'visible absolute right-2 top-2 z-10 aspect-square h-8 rounded-full bg-white/60 lg:invisible lg:h-10 lg:group-hover:visible',
              !isInCart ? 'lg:invisible lg:group-hover:visible' : ''
            )}
            id='add-to-wishlist'
            onClick={e => toggleWishlist(e)}>
            <Svg
              viewBox='0 0 16 16'
              className={twJoin(
                `mx-auto aspect-square h-4 cursor-pointer stroke-fig stroke-1 lg:h-5.5`,
                isWishlisted ? 'fill-fig' : 'fill-none'
              )}>
              <HeartOutlineIcon />
            </Svg>
          </button>

          {image && image !== '' ? (
            <div
              className={twMerge(
                'relative aspect-square w-full',
                selectedVariant?.stock_status === 'OUT_OF_STOCK'
                  ? 'opacity-50'
                  : ''
              )}>
              <Image
                alt={selectedVariant.name ?? 'Product'}
                title={selectedVariant.name}
                src={imageUrl}
                fill
                sizes='(max-width: 768px) 50vw, 30vw'
                placeholder='empty'
                className='rounded-lg object-cover'
              />
            </div>
          ) : (
            <div className='aspect-square h-full w-full rounded-md bg-[#FEF9F9]' />
          )}
          {item?.stock_status !== 'OUT_OF_STOCK' && (
            <ul
              className={twMerge(
                'absolute left-4 top-4 flex flex-col gap-2 rounded-sm',
                plpCard && 'max-md:left-2 max-md:top-2'
              )}>
              {tag_one && tag_one !== '' && (
                <li
                  key={'tag_one'}
                  className={twMerge(
                    'w-fit rounded-sm bg-[#38383A] px-2 py-1'
                  )}>
                  <p
                    className={twMerge(
                      'font-gilroy line-clamp-1 text-white',
                      plpCard ? 'max-md:text-xs' : 'text-base'
                    )}>
                    {tag_one}
                  </p>
                </li>
              )}
              {tag_two && tag_two !== '' && (
                <li
                  key={'tag_two'}
                  className={twMerge(
                    'w-fit rounded-sm bg-[#D48A7A] px-2 py-1'
                  )}>
                  <p
                    className={twMerge(
                      'font-gilroy line-clamp-1 text-white',
                      plpCard ? 'max-md:text-xs' : 'text-base'
                    )}>
                    {tag_two}
                  </p>
                </li>
              )}
            </ul>
          )}
        </div>
        <div
          className={twMerge(
            'h-[4.3rem] space-y-2 lg:h-14',
            plpCard ? 'h-[3.7rem] md:h-14' : ''
          )}
          aria-label='title-price'>
          <Heading
            className={twMerge(
              'line-clamp-2 px-0 text-left text-lg font-semibold leading-5.5 text-black',
              selectedVariant?.stock_status === 'OUT_OF_STOCK'
                ? 'opacity-50'
                : '',
              plpCard ? 'text-sm md:text-lg' : ''
            )}>
            {selectedVariant?.name}
          </Heading>
          <div className='flex w-full items-center justify-between'>
            <Text
              className={twMerge(
                'px-0 text-left text-lg font-semibold',
                selectedVariant?.stock_status === 'OUT_OF_STOCK'
                  ? 'opacity-50'
                  : '',
                plpCard ? 'text-sm font-bold md:text-lg' : ''
              )}>
              {customAttributes?.special_price ? (
                <>
                  <span>
                    ₹{Number(customAttributes?.special_price)?.toFixed(0)}
                  </span>
                  <span className='ml-3 font-light text-gray-400 line-through'>
                    ₹{Number(selectedVariant?.price)?.toFixed(0)}
                  </span>
                </>
              ) : (
                <span>₹{Number(selectedVariant?.price)?.toFixed(0)}</span>
              )}
            </Text>
            {selectedVariant?.typeId === PRODUCT_TYPE_ENUMS.configurable ? (
              <div
                className='px-3'
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}>
                <SelectOptionProductCard
                  getSelected={option => handleVariantChange(option)}
                  options={variantsOptions}
                  defaultValue={
                    variantsOptions?.length ? variantsOptions[0].weight : {}
                  }
                  sku={item?.sku}
                />
              </div>
            ) : (
              <Text
                className={twMerge(
                  'px-0 text-left text-lg font-semibold text-black',
                  selectedVariant?.stock_status === 'OUT_OF_STOCK'
                    ? 'opacity-50'
                    : '',
                  plpCard ? 'text-sm md:text-lg' : ''
                )}>
                {getWeightWithUnit(
                  selectedVariant?.weight,
                  selectedVariant?.weight_unit || customAttributes?.weight_unit
                )}
              </Text>
            )}
          </div>
        </div>
      </Link>
      <div
        className={twMerge(
          'visible flex items-center justify-start gap-y-1',
          buttonProps.label !== 'Added to Cart'
            ? 'lg:invisible lg:group-hover:visible'
            : 'h-full',
          selectedVariant?.stock_status === 'OUT_OF_STOCK' ? 'opacity-50' : ''
        )}>
        <UserActions />
      </div>
    </div>
  );
};

export default ProductCard;
